import { Container, Form, Spinner, Table } from 'react-bootstrap';
import React, { useEffect, useState } from 'react';
import {
  getSales,
  getSrikandiChildren,
  getSrikandiSales,
  getSrikandiSummarySales,
  useSrikandiUser,
} from '../../API';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { ISale, ISummarySale, IUser } from '../../@types';
import DatePicker, { DateObject } from 'react-multi-date-picker';
import { useAuth } from '../../Context/AuthContext';
import { get, getDatabase, ref } from 'firebase/database';
import { firebaseApp } from '../../index';

const formatDate = (date: Date) => date.toISOString().slice(0, 10);

const formatParamDate = (date: Date) => date.toISOString().slice(0, 10).replaceAll('-', '');
const format = (date: string) => `${date.slice(0, 4)}-${date.slice(4, 6)}-${date.slice(6, 8)}`;

const serialiseSearchParams = (params: any) => {
  return Object.keys(params)
    .map((e) => `${e}=${params[e]}`)
    .join('&');
};

const DailySales = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const start = searchParams.get('from');
  const end = searchParams.get('till');
  const startDate = start ? new Date(format(start)) : new Date();
  const endDate = end ? new Date(format(end)) : new Date();
  const [children, setChildren] = useState<IUser[]>([]);
  const [busy, setBusy] = useState(false);
  const [isDateSelectorClosed, setIsDateSelectorClosed] = useState(true);
  const [sales, setSales] = useState<ISale[]>([]);
  const [summarySales, setSummarySales] = useState<ISummarySale[]>([]);
  const [filterDate, setFilterDate] = useState('');
  const { user: currentUser, dbUser } = useAuth();
  const { userId } = useParams();
  const user = useSrikandiUser(userId || currentUser?.uid);
  const isSummary = window.location.pathname.includes('summary');
  const [open, setOpen] = React.useState<{ [index: number]: boolean }>({});

  useEffect(() => {
    if (!start || !end) {
      const params = {
        from: formatParamDate(new Date()),
        till: formatParamDate(new Date()),
      };
      setSearchParams(serialiseSearchParams(params));
    }
  }, [start, end]);

  useEffect(() => {
    if (dbUser) {
      if (dbUser.role === 'admin') {
        const database = getDatabase(firebaseApp);
        const usersRef = ref(database, 'users');
        get(usersRef).then((usersSnapshot) => {
          let userList: any[] = [];
          usersSnapshot.forEach((userSnapshot) => {
            userList = userList.concat({
              id: userSnapshot.key,
              ...userSnapshot.val(),
            });
          });
          setChildren(
            userList
              .filter((user) => user.role !== 'deactivated' && user.name) // Check if user.role is not 'deactivated' and user.name is defined
              .sort((a, b) => a.name.localeCompare(b.name)),
          );
        });
      } else {
        getSrikandiChildren(dbUser.id).then(setChildren);
      }
    }
  }, [dbUser]);

  const handleDatesChanged = (dates: DateObject[]) => {
    const [start, end] = dates.map((e) => e.toDate());
    const params = {
      from: formatParamDate(start),
      till: formatParamDate(end),
    };
    setSearchParams(serialiseSearchParams(params));
  };

  useEffect(() => {
    if (user && startDate && endDate && isDateSelectorClosed) {
      setBusy(true);
      if (isSummary) {
        getSrikandiSummarySales(user.id, formatDate(startDate), formatDate(endDate)).then(
          (data) => {
            setBusy(false);
            setSummarySales(data);
          },
        );
      } else {
        // if has userid (from url params) query just individual sales, else group sales
        const getSalesContainer = userId ? getSales : getSrikandiSales;
        getSalesContainer(user.id, formatDate(startDate), formatDate(endDate)).then((data) => {
          setBusy(false);
          setSales(data);
          // console.log(data)
        });
      }
    }
  }, [userId, user, start, end, isDateSelectorClosed, isSummary]);

  const handleFilter = () => {};

  const getOrderStatus = (sale: ISale) => {
    if (sale.cancelled_at) {
      return 'Cancelled';
    }
    if (sale.fulfilled_at) {
      return 'Fulfilled';
    }
    return 'Paid';
  };

  const groupSales = (sales: ISale[]) => {
    const grouped = sales.reduce((acc, curr) => {
      // if(!curr.event_name){
        acc[curr.order_id] = (acc[curr.order_id] || []).concat(curr);
      // }
      return acc;
    }, {} as { [orderId: string]: ISale[] });
    return Object.keys(grouped).map((e) => {
      const items = grouped[e];
      console.log(items);
      return {
        orderId: items[0].order_name || e,
        source: items[0].type,
        email: items[0].customer_email,
        itemCount: items.length,
        orderDate: items[0].order_datetime,
        orderSales: parseFloat(items[0].order_total_sale),
        orderCurrency: items[0].order_currency,
        orderStatus: getOrderStatus(items[0]),
        items,
      };
    });
  };

  if (!user) {
    return (
      <Container>
        <Spinner
          animation="border"
          role="status"
          className="text-center"
          style={{
            position: 'absolute',
            height: '80px',
            width: '80px',
            top: '50%',
            left: '50%',
            marginLeft: '-50px',
            marginTop: '-30px',
          }}
        >
          <span className="visually-hidden">Loading...</span>
        </Spinner>
      </Container>
    );
  }

  const groupedSales = groupSales(sales);

  const handleExpand = (index: number) => {
    setOpen((prevState) => ({
      ...prevState,
      [index]: !prevState[index],
    }));
  };

  const renderSales = () => {
    if (busy) {
      return (
        <Container>
          <Spinner animation="border" role="status" className="text-center">
            <span className="visually-hidden">Loading...</span>
          </Spinner>
        </Container>
      );
    }
    if (isSummary) {
      if (summarySales.length === 0) {
        return <span>No data</span>;
      }
      let summaryTotalSale = 0;
      return (
        <Table bordered hover size="sm" responsive>
          <thead className="tbl-header">
            <tr>
              <th>#</th>
              <th>Email</th>
              <th>Sale</th>
            </tr>
          </thead>
          <tbody className="tbl-content">
            {/* todo: item can be clicked and expanded to view order item details (exist inside .items)*/}
            {summarySales.map((e, i) => {
              summaryTotalSale += parseFloat(e.total_sale);
              return (
                <tr key={i}>
                  <td>{i + 1}</td>
                  <td>{e.email}</td>
                  <td className="midTitle">{'RM' + ' ' + parseFloat(e.total_sale).toFixed(2)}</td>
                </tr>
              );
            })}
            <tr>
              <td></td>
              <td className="smallTitle">Total</td>
              <td className="bold400 centerItem">{'RM' + ' ' + summaryTotalSale.toFixed(2)}</td>
            </tr>
          </tbody>
        </Table>
      );
    }
    if (sales.length === 0) {
      return <span>No data</span>;
    }
    return (
      <Table bordered hover size="sm" responsive>
        <thead className="tbl-header">
          <tr>
            <th className="capitalize">Order ID</th>
            <th className="capitalize">Source</th>
            <th className="capitalize" style={{ width: '25%' }}>
              Email
            </th>
            <th className="capitalize" style={{ width: '7.5%' }}>
              # Items
            </th>
            <th className="capitalize">Order Status</th>
            <th className="capitalize" style={{ width: '16%' }}>
              Order Date
            </th>
            <th className="capitalize">Order Sales</th>
          </tr>
        </thead>
        <tbody className="tbl-content" style={{ cursor: 'pointer' }}>
          {/* todo: item can be clicked and expanded to view order item details (exist inside .items)*/}
          {groupedSales.map((e, index) => {
            if (open[index]) {
              return [
                <tr key={e.orderId} onClick={() => handleExpand(index)} id="orderDetail">
                  <td rowSpan={e.items.length + 1} className="bold400 centerItem">
                    {e.orderId}
                  </td>
                  <td className="centerItem">
                    {e.source === 'woocommerce' ? 'Minisite' : 'Website'}
                  </td>
                  <td className="centerItem">{e.email}</td>
                  <td className="quantity centerItem">{e.itemCount}</td>
                  <td className="centerItem">{e.orderStatus}</td>
                  <td className="centerItem">{e.orderDate}</td>
                  <td className="bold400 centerItem">{'RM' + ' ' + e.orderSales.toFixed(2)}</td>
                </tr>,
                ...e.items.map((item) => (
                  <tr key={e.orderId + item.id}>
                    <td
                      colSpan={5}
                      style={{
                        textAlign: 'right',
                        color: 'var(--accentColor)',
                        backgroundColor: 'var(--childColor)',
                      }}
                    >
                      {item.order_item_title}
                    </td>
                    <td
                      className="centerItem"
                      style={{ color: 'var(--accentColor)', backgroundColor: 'var(--childColor)' }}
                    >
                      {'RM' + ' ' + parseFloat(item.order_item_sale).toFixed(2)}
                    </td>
                  </tr>
                )),
              ];
            }
            return (
              <tr key={e.orderId} onClick={() => handleExpand(index)}>
                <td className="bold400 centerItem">{e.orderId}</td>
                <td className="centerItem">
                  {e.source === 'woocommerce' ? 'Minisite' : 'Website'}
                </td>
                <td className="centerItem">{e.email}</td>
                <td className="quantity centerItem">{e.itemCount}</td>
                <td className="centerItem">{e.orderStatus}</td>
                <td className="centerItem">{e.orderDate}</td>
                <td className="bold400 centerItem">{'RM' + ' ' + e.orderSales.toFixed(2)}</td>
              </tr>
            );
          })}

          <tr>
            <td></td>
            <td></td>
            <td></td>
            <td></td>
            <td></td>
            <td className="smallTitle centerItem">Total</td>
            <td className="smallTitle centerItem">
              {'RM' +
                ' ' +
                groupedSales
                  .reduce(
                    (acc, curr) => (curr.orderStatus !== 'Cancelled' ? acc + curr.orderSales : acc),
                    0,
                  )
                  .toFixed(2)}
            </td>
          </tr>
        </tbody>
      </Table>
    );
  };

  const handleSaleUserChange = (event: any) => {
    const userId = event.target.value;
    const params = {
      from: start,
      till: end,
    };
    if (userId === 'null') {
      navigate(`/sales?${serialiseSearchParams(params)}`);
    } else if (userId === 'summary') {
      navigate(`/sales-summary?${serialiseSearchParams(params)}`);
    } else {
      navigate(`/srikandi/${userId}/sales?${serialiseSearchParams(params)}`);
    }
  };

  return (
    <Container>
      {/* User information */}
      <div className=" centerItem">
        <h2 className="header">User Details</h2>
        <br></br>
        <Form.Group className="mb-3">
          <Form.Label htmlFor="outletSelect">
            <b className="smallTitle">Select User</b>
          </Form.Label>
          <br></br>
          <Form.Select
            id="outletSelect"
            onChange={handleSaleUserChange}
            value={userId ? userId : isSummary ? 'summary' : 'null'}
            className="selectBox centerItem"
          >
            {/* <option value={currentUser?.uid}>Personal</option> */}
            <option className="text " value="null">
              Team Sale
            </option>
            {/* <option value="summary">Team Summary</option> */}
            {/* {children.map((e) => (
              <option key={e.id} value={e.id}>{`${e.name} (${e.email})`}</option>
            ))} */}
          </Form.Select>
        </Form.Group>
        <Table className="mobileWidth">
          <tr className="tables" style={{ borderWidth: '0px' }}>
            <td style={{ borderBottom: '0px' }}>
              <p>
                {' '}
                <b className="smallTitle">Coupon</b>
                <br></br>
                <b className="text"> {user.coupon}</b>
              </p>
            </td>
            <td style={{ borderBottom: '0px' }}>
              <p>
                <b className="smallTitle">Name</b>
                <br></br>
                <b className="text"> {user.name}</b>
              </p>
            </td>
            <td style={{ borderBottom: '0px' }}>
              <p>
                <b className="smallTitle">Email</b>
                <br></br>
                <b className="text"> {user.email}</b>
              </p>
            </td>
            <td style={{ borderBottom: '0px' }}>
              <p>
                <b className="smallTitle">Date Joined</b>
                <br></br>
                <b className="text"> {new Date(user.registered_at).toISOString()}</b>
              </p>
            </td>
          </tr>
        </Table>
        <p>
          <b className="smallTitle">Sale interval</b>
          <br></br>
          <b className="text">
            {' '}
            <DatePicker
              onOpen={() => setIsDateSelectorClosed(false)}
              onClose={() => setIsDateSelectorClosed(true)}
              range
              value={[startDate, endDate]}
              onChange={handleDatesChanged}
              style={{
                border: '1px solid var(--primaryColor)',
                height: '30px',
                width: '200px',
                padding: '2px 20px',
              }}
            />
          </b>
        </p>
      </div>
      <div className=" centerItem">
        <br></br>
        {/* <h2 className="header">Total Sales</h2> */}
        {renderSales()}
      </div>
      <br></br>
      <br></br>
    </Container>
  );
};

export default DailySales;
